import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Edit,
  Trash2,
  Car,
  FileText,
  ChevronLeft,
  ChevronRight,
  Instagram,
  Youtube,
  Facebook,
  Video,
  Share2,
  Settings,
  Shield,
  Check,
  ExternalLink,
  Building,
  Clock,
  FileCheck,
  Eye,
  EyeOff,
} from 'lucide-react'
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  ConfirmDialog,
  ImageLightbox,
  VehicleDetailsPageSkeleton,
  Toast,
  ToastType,
  TimelineHistory,
} from '../../components/ui'
import { VehicleExistingDocuments } from '../../components/vehicles'
import { apiService } from '../../services/api'
import { getVehicleFeatureGroupsForCategory, useVehicleOptions } from '../../hooks/useVehicleOptions'
import { useVehicleHistory } from '../../hooks/useVehicleHistory'
import { cn } from '../../lib/utils'

interface VehicleDetail {
  basic: {
    id: string
    name: string
    category: string
    condition: string
    status: string
    type: string
    description: string
  }
  catalog: {
    brand: {
      id: string
      name: string
      slug: string
      logoUrl?: string
      country: string
    }
    model: {
      id: string
      name: string
      slug: string
      category: string
      type: string
      startYear: number
      endYear: number | null
    }
    version: {
      id: string
      name: string
      slug: string
      modelYear: number
      fuelType: string
      transmission: string
      engine: string
      powerHp: number
      doors: number
      seats: number
    }
    modelYear: number
    manufactureYear: number
  }
  identification: {
    plate: string
    plateEnd: string
    chassis: string
    renavam: string
  }
  specifications: {
    engine: {
      type: string
      transmission: string
      engine: string | null
      powerHp: number | null
      displacement: number | null
      cylinders: number | null
      turbo: boolean
    }
    consumption: {
      city: number | null
      highway: number | null
      tankCapacity: number | null
    }
    electric: any | null
    motorcycle: any | null
    truck: any | null
    bus: any | null
    body: {
      doors: number
      seats: number | null
      color: string
      colorName: string | null
      secondaryColor: string | null
      secondaryColorName: string | null
    }
    mileage: number
  }
  commercial: {
    inspected: boolean
    inspectionDate: string
    videoUrl: string
    acceptsTrade: boolean
    armorLevel: string
    specialCategory: string
    steeringType: string
    warranty: {
      months: number | null
      endDate: string | null
    }
  }
  auction: {
    isAuction: boolean
    category: string
    subtype: string
    date: string | null
    house: string | null
    lot: string | null
    damageLevel: string
    notes: string | null
  }
  pricing: {
    purchase: {
      price: number | null
      date: string | null
    }
    sale: {
      price: string
    }
    sold: any | null
  }
  features?: Record<string, boolean | null | number | string>
  featuresByCategory?: Record<string, Record<string, boolean | null | number | string> | null>
  characteristics: string[]
  media: {
    photos: Array<{
      id: string
      url: string
      thumbnail: string | null
      order: number
      isPrimary: boolean
      uploadedBy: { id: string; name: string }
      createdAt: string
    }>
    documents: Array<{
      id: string
      type: string
      name: string
      url: string
      expiryDate?: string
      uploadedBy: { id: string; name: string; email: string }
      createdAt: string
    }>
    socialMedia: Array<{
      id: string
      type: string
      url: string
    }>
  }
  history: { maintenance: any[]; documents: any[] }
  proposals: any[]
  dealership: {
    id: string
    name: string
    tradeName: string
    email: string
    phone: string
    address: string
  }
  metadata: {
    createdBy: { id: string; name: string; email: string }
    updatedBy: { id: string; name: string; email: string }
    createdAt: string
    updatedAt: string
    deletedAt: string | null
  }
  counts: {
    photos: number
    socialMedia: number
    characteristics: number
    maintenanceRecords: number
    documents: number
    proposals: number
  }
}

const STATUS_CONFIG: Record<string, { label: string; classes: string }> = {
  draft: { label: 'Rascunho', classes: 'bg-gray-500/10 text-gray-700 border-gray-500/20' },
  in_stock: { label: 'Em Estoque', classes: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20' },
  reserved: { label: 'Reservado', classes: 'bg-amber-500/10 text-amber-700 border-amber-500/20' },
  sold: { label: 'Vendido', classes: 'bg-surface text-foreground border-border' },
  in_transit: { label: 'Em Trânsito', classes: 'bg-blue-500/10 text-blue-700 border-blue-500/20' },
  in_maintenance: { label: 'Em Manutenção', classes: 'bg-orange-500/10 text-orange-700 border-orange-500/20' },
  consignment: { label: 'Consignado', classes: 'bg-purple-500/10 text-purple-700 border-purple-500/20' },
}

const CHAR_LABELS: Record<string, string> = {
  ipva_paid: 'IPVA Pago',
  licensed: 'Licenciado',
  all_revisions_done: 'Revisões em Dia',
  single_owner: 'Único Dono',
  dealership_maintained: 'Revisão em Concessionária',
  accident_free: 'Sem Acidentes',
  no_paint: 'Sem Pintura',
  original_manual: 'Manual Original',
  duplicate_key: 'Chave Reserva',
  warranty: 'Com Garantia',
  factory_warranty: 'Garantia de Fábrica',
  extended_warranty: 'Garantia Estendida',
  accepts_trade: 'Aceita Troca',
  alienated: 'Alienado',
}

const FEATURE_CATEGORIES = [
  { key: 'comfort', label: 'Conforto' },
  { key: 'technology', label: 'Tecnologia' },
  { key: 'safety', label: 'Segurança' },
  { key: 'adas', label: 'Sistemas Avançados (ADAS)' },
  { key: 'lighting', label: 'Iluminação' },
  { key: 'convenience', label: 'Conveniência' },
  { key: 'performance', label: 'Performance' },
]

type TabId = 'details' | 'specs' | 'features' | 'characteristics' | 'history'

const TABS: { id: TabId; label: string }[] = [
  { id: 'details', label: 'Detalhes' },
  { id: 'specs', label: 'Especificações' },
  { id: 'features', label: 'Equipamentos' },
  { id: 'characteristics', label: 'Características' },
  { id: 'history', label: 'Histórico' },
]

/* ─── Small helpers ─── */
const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div>
    <dt className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-0.5">
      {label}
    </dt>
    <dd className="text-sm font-medium text-foreground">{value}</dd>
  </div>
)

const SectionHeading = ({
  icon: Icon,
  title,
}: {
  icon?: React.ComponentType<{ className?: string }>
  title: string
}) => (
  <div className="flex items-center gap-2 mb-4 pb-2 border-b border-border">
    {Icon && <Icon className="h-4 w-4 text-accent" />}
    <h3 className="text-sm font-semibold text-foreground">{title}</h3>
  </div>
)

export const VehicleDetailsPage: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [vehicle, setVehicle] = useState<VehicleDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<TabId>('details')
  const [visibleFields, setVisibleFields] = useState({ plate: false, chassis: false, renavam: false })
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)
  const { options: vehicleOptions } = useVehicleOptions()
  const { history, isLoading: historyLoading, error: historyError } = useVehicleHistory(id)

  const featureGroupsForVehicle = useMemo(
    () => getVehicleFeatureGroupsForCategory(vehicleOptions, vehicle?.basic.category),
    [vehicleOptions, vehicle?.basic.category],
  )

  const resolvedVehicleFeatures = useMemo((): Record<string, boolean | null | number | string> => {
    if (!vehicle) return {}
    const cat = vehicle.basic?.category
    const byCat = vehicle.featuresByCategory?.[cat]
    if (byCat && typeof byCat === 'object') return byCat as Record<string, boolean | null | number | string>
    return vehicle.features ?? {}
  }, [vehicle])

  useEffect(() => {
    if (id) fetchVehicle(id)
  }, [id])

  const fetchVehicle = async (vehicleId: string) => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await apiService.getVehicleById(vehicleId)
      setVehicle(response as any)
    } catch {
      setError('Erro ao carregar veículo')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!vehicle) return
    try {
      await apiService.deleteVehicle(vehicle.basic.id)
      navigate('/vehicles', { state: { successMessage: 'Veículo excluído com sucesso!' } })
    } catch (err: any) {
      setToast({ message: err?.message || 'Erro ao excluir veículo', type: 'error' })
    }
  }

  const handleActivate = async () => {
    if (!vehicle) return
    try {
      await apiService.activateVehicle(vehicle.basic.id)
      setToast({ message: 'Veículo ativado com sucesso', type: 'success' })
      await fetchVehicle(vehicle.basic.id)
    } catch (err: any) {
      setToast({ message: err.message || 'Erro ao ativar veículo', type: 'error' })
    }
  }

  const nextPhoto = () => {
    if (vehicle?.media?.photos && currentPhotoIndex < vehicle.media.photos.length - 1)
      setCurrentPhotoIndex((i) => i + 1)
  }
  const prevPhoto = () => {
    if (currentPhotoIndex > 0) setCurrentPhotoIndex((i) => i - 1)
  }

  const formatPrice = (price: string | number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
      typeof price === 'string' ? parseFloat(price) : price,
    )

  const getOpt = <T extends { value: string; label: string }>(
    list: T[] | undefined,
    value: string,
  ) => list?.find((o) => o.value === value)?.label || value

  const maskString = (str: string | null | undefined, showLast = false) => {
    if (!str) return ''
    if (showLast && str.length > 0) return '•'.repeat(str.length - 1) + str.charAt(str.length - 1)
    return '•'.repeat(str.length)
  }

  const toggleField = (f: keyof typeof visibleFields) =>
    setVisibleFields((prev) => ({ ...prev, [f]: !prev[f] }))

  const getSocialIcon = (type: string) => {
    const map: Record<string, React.ReactNode> = {
      instagram: <Instagram className="h-4 w-4" />,
      youtube: <Youtube className="h-4 w-4" />,
      facebook: <Facebook className="h-4 w-4" />,
      tiktok: <Video className="h-4 w-4" />,
    }
    return map[type] ?? <Share2 className="h-4 w-4" />
  }

  /* ── Derived auction subtype lookup ── */
  const getAuctionSubtypeLabel = (category: string, value: string) => {
    const keyMap: Record<string, keyof typeof vehicleOptions.auctions> = {
      insurance: 'insurance',
      bank: 'bank',
      judicial: 'judicial',
      federal: 'federal',
      police: 'police',
      fleet: 'fleet',
      private: 'private',
    }
    const list = vehicleOptions.auctions?.[keyMap[category]] as any[] | undefined
    return list?.find((s: any) => s.value === value)?.label || value
  }

  if (isLoading) {
    return <VehicleDetailsPageSkeleton />
  }

  if (error || !vehicle)
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <Button variant="outline" onClick={() => navigate('/vehicles')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <p className="text-red-700 text-sm">{error || 'Veículo não encontrado'}</p>
        </div>
      </div>
    )

  const currentPhoto = vehicle.media?.photos?.[currentPhotoIndex]
  const photos = vehicle.media?.photos || []
  const statusCfg = STATUS_CONFIG[vehicle.basic.status] || STATUS_CONFIG.in_stock

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/vehicles')}>
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Voltar
          </Button>
          <div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-xl font-bold text-foreground font-display">{vehicle.basic.name}</h1>
              <span
                className={cn(
                  'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border',
                  statusCfg.classes,
                )}
              >
                {statusCfg.label}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">
              {getOpt(vehicleOptions.conditions, vehicle.basic.condition)} ·{' '}
              {vehicle.catalog.modelYear}/{vehicle.catalog.manufactureYear}
            </p>
          </div>
        </div>

        <div className="flex gap-2 flex-shrink-0">
          {vehicle.basic.status === 'draft' && (
            <Button
              size="sm"
              onClick={handleActivate}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Check className="h-4 w-4 mr-1.5" />
              Ativar Veículo
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/vehicles/edit/${vehicle.basic.id}`)}
          >
            <Edit className="h-4 w-4 mr-1.5" />
            Editar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeleteDialogOpen(true)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            Excluir
          </Button>
        </div>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* ── Left column ── */}
        <div className="lg:col-span-2 space-y-5">
          {/* Photo carousel */}
          <Card>
            <CardContent className="p-0">
              <div
                className="relative aspect-video bg-surface rounded-t-lg overflow-hidden cursor-pointer group"
                onClick={() => photos.length > 0 && setLightboxOpen(true)}
              >
                {photos.length > 0 ? (
                  <>
                    <img
                      src={currentPhoto?.url}
                      alt={`${vehicle.basic.name} — Foto ${currentPhotoIndex + 1}`}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                    />

                    {/* Enlarge hint */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-end justify-end p-3">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center gap-1.5 bg-foreground/70 text-white text-xs font-medium px-2.5 py-1.5 rounded-md backdrop-blur-sm">
                        <Eye className="h-3.5 w-3.5" />
                        Ampliar
                      </div>
                    </div>

                    {/* Nav arrows */}
                    {photos.length > 1 && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); prevPhoto() }}
                          disabled={currentPhotoIndex === 0}
                          className="absolute left-3 top-1/2 -translate-y-1/2 bg-card/80 hover:bg-card text-foreground p-2 rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-all z-10 backdrop-blur-sm border border-border/30"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); nextPhoto() }}
                          disabled={currentPhotoIndex === photos.length - 1}
                          className="absolute right-3 top-1/2 -translate-y-1/2 bg-card/80 hover:bg-card text-foreground p-2 rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-all z-10 backdrop-blur-sm border border-border/30"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>

                        {/* Counter */}
                        <div className="absolute bottom-3 left-3 bg-foreground/60 text-white text-xs px-2 py-1 rounded-md tabular-nums">
                          {currentPhotoIndex + 1} / {photos.length}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                    <Car className="h-14 w-14 mb-2 opacity-30" />
                    <p className="text-sm">Sem fotos disponíveis</p>
                  </div>
                )}
              </div>

              {/* Thumbnails */}
              {photos.length > 1 && (
                <div className="p-3 grid grid-cols-8 gap-1.5 border-t border-border">
                  {photos.map((photo, index) => (
                    <button
                      key={photo.id}
                      onClick={() => setCurrentPhotoIndex(index)}
                      className={cn(
                        'aspect-video rounded overflow-hidden border-2 transition-all',
                        currentPhotoIndex === index
                          ? 'border-accent ring-1 ring-accent/30'
                          : 'border-transparent hover:border-border',
                      )}
                    >
                      <img
                        src={photo.url}
                        alt={`Miniatura ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Lightbox */}
          {photos.length > 0 && (
            <ImageLightbox
              images={photos.map((p) => p.url)}
              currentIndex={currentPhotoIndex}
              isOpen={lightboxOpen}
              onClose={() => setLightboxOpen(false)}
              onNavigate={setCurrentPhotoIndex}
            />
          )}

          {/* Existing documents */}
          {vehicle.media?.documents && vehicle.media.documents.length > 0 && (
            <VehicleExistingDocuments documents={vehicle.media.documents} />
          )}

          {/* Tabs */}
          <Card>
            <CardHeader className="p-0 border-b border-border">
              <div className="flex px-5 pt-3 gap-5 overflow-x-auto">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'pb-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors',
                      activeTab === tab.id
                        ? 'border-accent text-accent'
                        : 'border-transparent text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </CardHeader>

            <CardContent className="p-5">
              {/* ── Details tab ── */}
              {activeTab === 'details' && (
                <div className="space-y-6">
                  {vehicle.basic.description && (
                    <div>
                      <SectionHeading title="Descrição" />
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {vehicle.basic.description}
                      </p>
                    </div>
                  )}

                  <div>
                    <SectionHeading icon={Car} title="Informações Básicas" />
                    <dl className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <DetailRow
                        label="Marca"
                        value={
                          <span className="flex items-center gap-1.5">
                            {vehicle.catalog.brand.logoUrl && (
                              <img
                                src={vehicle.catalog.brand.logoUrl}
                                alt={vehicle.catalog.brand.name}
                                className="h-4 w-4 object-contain"
                              />
                            )}
                            {vehicle.catalog.brand.name}
                          </span>
                        }
                      />
                      <DetailRow label="Modelo" value={vehicle.catalog.model.name} />
                      <DetailRow label="Versão" value={vehicle.catalog.version.name} />
                      <DetailRow
                        label="Ano Modelo / Fab."
                        value={`${vehicle.catalog.modelYear} / ${vehicle.catalog.manufactureYear}`}
                      />
                      <DetailRow
                        label="Quilometragem"
                        value={`${vehicle.specifications.mileage?.toLocaleString('pt-BR')} km`}
                      />
                      <DetailRow
                        label="Cor"
                        value={getOpt(vehicleOptions.colors, vehicle.specifications.body.color)}
                      />
                    </dl>
                  </div>

                  {/* Documentation */}
                  <div>
                    <SectionHeading icon={FileText} title="Documentação" />
                    <div className="space-y-2">
                      {(
                        [
                          { field: 'plate' as const, label: 'Placa', showLast: true },
                          { field: 'chassis' as const, label: 'Chassi', showLast: false },
                          { field: 'renavam' as const, label: 'RENAVAM', showLast: false },
                        ] as const
                      ).map(({ field, label, showLast }) => {
                        const raw = vehicle.identification[field]
                        return (
                          <div
                            key={field}
                            className="flex items-center justify-between p-3 bg-surface rounded-lg border border-border"
                          >
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-0.5">
                                {label}
                              </p>
                              <p className="text-sm font-mono font-medium text-foreground">
                                {raw ? (
                                  visibleFields[field] ? raw : maskString(raw, showLast)
                                ) : (
                                  <span className="text-muted-foreground italic text-xs">
                                    Não informado
                                  </span>
                                )}
                              </p>
                            </div>
                            <button
                              onClick={() => toggleField(field)}
                              className="p-2 hover:bg-border rounded-lg transition-colors"
                            >
                              {visibleFields[field] ? (
                                <EyeOff className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <Eye className="h-4 w-4 text-muted-foreground" />
                              )}
                            </button>
                          </div>
                        )
                      })}
                    </div>

                    {vehicle.commercial.inspected && (
                      <div className="mt-3 flex items-center gap-2.5 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <FileCheck className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-emerald-800">Veículo Vistoriado</p>
                          <p className="text-xs text-emerald-600">
                            {new Date(vehicle.commercial.inspectionDate).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Auction */}
                  {vehicle.auction?.isAuction && (
                    <div>
                      <SectionHeading title="Informações de Leilão" />
                      <dl className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <DetailRow
                          label="Categoria"
                          value={getOpt(vehicleOptions.auctions?.categories, vehicle.auction.category)}
                        />
                        <DetailRow
                          label="Subtipo"
                          value={getAuctionSubtypeLabel(vehicle.auction.category, vehicle.auction.subtype)}
                        />
                        <DetailRow
                          label="Nível de Dano"
                          value={getOpt(vehicleOptions.auctions?.damageLevels, vehicle.auction.damageLevel)}
                        />
                      </dl>
                    </div>
                  )}
                </div>
              )}

              {/* ── Specs tab ── */}
              {activeTab === 'specs' && (
                <div className="space-y-6">
                  <div>
                    <SectionHeading icon={Settings} title="Motor e Transmissão" />
                    <dl className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <DetailRow
                        label="Combustível"
                        value={getOpt(vehicleOptions.fuelTypes, vehicle.specifications.engine.type)}
                      />
                      <DetailRow
                        label="Transmissão"
                        value={getOpt(vehicleOptions.transmissions, vehicle.specifications.engine.transmission)}
                      />
                      {vehicle.catalog.version.engine && (
                        <DetailRow label="Motor" value={vehicle.catalog.version.engine} />
                      )}
                      {vehicle.catalog.version.powerHp > 0 && (
                        <DetailRow label="Potência" value={`${vehicle.catalog.version.powerHp} cv`} />
                      )}
                    </dl>
                  </div>

                  <div>
                    <SectionHeading icon={Car} title="Carroceria" />
                    <dl className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <DetailRow
                        label="Tipo"
                        value={getOpt(vehicleOptions.bodyTypes, vehicle.basic.type)}
                      />
                      <DetailRow label="Portas" value={vehicle.specifications.body.doors} />
                      {vehicle.catalog.version.seats > 0 && (
                        <DetailRow label="Lugares" value={vehicle.catalog.version.seats} />
                      )}
                      <DetailRow
                        label="Direção"
                        value={getOpt(vehicleOptions.steeringTypes, vehicle.commercial.steeringType)}
                      />
                    </dl>
                  </div>

                  {vehicle.basic.category === 'C' && vehicle.specifications.truck && (
                    <div>
                      <SectionHeading icon={Settings} title="Detalhes de Caminhão" />
                      <dl className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <DetailRow label="Carroceria" value={vehicle.specifications.truck.bodyType || '-'} />
                        <DetailRow label="Cabine" value={vehicle.specifications.truck.cabType || '-'} />
                        <DetailRow label="Tração" value={vehicle.specifications.truck.traction || '-'} />
                        <DetailRow label="Eixos" value={vehicle.specifications.truck.axles ?? '-'} />
                        <DetailRow label="PBT (kg)" value={vehicle.specifications.truck.grossVehicleWeight ?? '-'} />
                        <DetailRow label="Carga (kg)" value={vehicle.specifications.truck.payload ?? '-'} />
                      </dl>
                    </div>
                  )}

                  {vehicle.basic.category === 'D' && vehicle.specifications.bus && (
                    <div>
                      <SectionHeading icon={Settings} title="Detalhes de Ônibus" />
                      <dl className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <DetailRow label="Marca Carroceria" value={vehicle.specifications.bus.bodyBrand || '-'} />
                        <DetailRow label="Modelo Carroceria" value={vehicle.specifications.bus.bodyModel || '-'} />
                        <DetailRow label="Operação" value={vehicle.specifications.bus.operationType || '-'} />
                        <DetailRow label="Nº Frota" value={vehicle.specifications.bus.fleetNumber || '-'} />
                        <DetailRow label="Sentados" value={vehicle.specifications.bus.seatedCapacity ?? '-'} />
                        <DetailRow label="Em pé" value={vehicle.specifications.bus.standingCapacity ?? '-'} />
                      </dl>
                    </div>
                  )}

                  {(vehicle.commercial.armorLevel || vehicle.commercial.specialCategory) && (
                    <div>
                      <SectionHeading icon={Shield} title="Informações Adicionais" />
                      <dl className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {vehicle.commercial.armorLevel &&
                          vehicle.commercial.armorLevel !== 'none' && (
                            <DetailRow
                              label="Blindagem"
                              value={getOpt(vehicleOptions.armorLevels, vehicle.commercial.armorLevel)}
                            />
                          )}
                        {vehicle.commercial.specialCategory &&
                          vehicle.commercial.specialCategory !== 'none' && (
                            <DetailRow
                              label="Categoria Especial"
                              value={getOpt(
                                vehicleOptions.specialCategories,
                                vehicle.commercial.specialCategory,
                              )}
                            />
                          )}
                      </dl>
                    </div>
                  )}
                </div>
              )}

              {/* ── Features tab ── */}
              {activeTab === 'features' && (
                <div className="space-y-6">
                  {resolvedVehicleFeatures && Object.keys(resolvedVehicleFeatures).length > 0 ? (
                    FEATURE_CATEGORIES.map((cat) => {
                      const catFeatures =
                        (featureGroupsForVehicle[
                          cat.key as keyof typeof featureGroupsForVehicle
                        ] as { value: string; label: string }[]) || []
                      const active = catFeatures.filter(
                        (f: any) => resolvedVehicleFeatures[f.value] === true,
                      )
                      if (active.length === 0) return null
                      return (
                        <div key={cat.key}>
                          <SectionHeading title={cat.label} />
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {active.map((feature: any) => (
                              <div
                                key={feature.value}
                                className="flex items-center gap-2 p-2.5 bg-surface rounded-lg border border-border"
                              >
                                <div className="h-4 w-4 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                                  <Check className="h-2.5 w-2.5 text-emerald-600" />
                                </div>
                                <span className="text-xs text-foreground">{feature.label}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    })
                  ) : (
                    <div className="text-center py-10">
                      <p className="text-sm text-muted-foreground">Nenhum equipamento cadastrado</p>
                    </div>
                  )}
                </div>
              )}

              {/* ── Characteristics tab ── */}
              {activeTab === 'characteristics' && (
                <div>
                  {vehicle.characteristics && vehicle.characteristics.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {vehicle.characteristics.map((char, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent/10 text-accent border border-accent/20 rounded-full text-xs font-medium"
                        >
                          <Check className="h-3 w-3" />
                          {CHAR_LABELS[char] || char}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-10">
                      <p className="text-sm text-muted-foreground">Nenhuma característica cadastrada</p>
                    </div>
                  )}
                </div>
              )}

              {/* ── History tab ── */}
              {activeTab === 'history' && (
                <div>
                  {historyError ? (
                    <div className="text-center py-10">
                      <p className="text-sm text-destructive">Erro ao carregar histórico: {historyError}</p>
                    </div>
                  ) : (
                    <TimelineHistory
                      entries={history}
                      loading={historyLoading}
                      emptyMessage="Nenhuma atividade registrada ainda"
                    />
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ── Right sidebar ── */}
        <div className="space-y-4">
          {/* Price card */}
          <Card>
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">
                Preço de venda
              </p>
              <p
                className="text-3xl font-bold text-foreground tabular-nums mb-4"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                {formatPrice(vehicle.pricing.sale.price)}
              </p>

              {vehicle.commercial.acceptsTrade && (
                <div className="mb-3 flex items-center gap-2 p-2.5 bg-accent/5 border border-accent/20 rounded-lg">
                  <Check className="h-4 w-4 text-accent flex-shrink-0" />
                  <p className="text-sm font-medium text-accent">Aceita Troca</p>
                </div>
              )}

              <Button
                className="w-full"
                onClick={() =>
                  navigate(`/proposals/new?vehicleId=${vehicle.basic.id}`)
                }
              >
                <FileText className="h-4 w-4 mr-2" />
                Fazer Proposta
              </Button>
            </CardContent>
          </Card>

          {/* Media links */}
          {((vehicle.media?.socialMedia && vehicle.media.socialMedia.length > 0) ||
            vehicle.commercial.videoUrl) && (
            <Card>
              <CardHeader className="pb-0">
                <CardTitle className="text-sm">Mídias</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-2">
                {vehicle.commercial.videoUrl && (
                  <a
                    href={vehicle.commercial.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2.5 p-2.5 bg-surface rounded-lg border border-border hover:border-accent/30 hover:bg-accent/5 transition-colors"
                  >
                    <Video className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground flex-1">Ver Vídeo</span>
                    <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                  </a>
                )}
                {vehicle.media?.socialMedia?.map((social) => (
                  <a
                    key={social.id}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2.5 p-2.5 bg-surface rounded-lg border border-border hover:border-accent/30 hover:bg-accent/5 transition-colors"
                  >
                    <span className="text-muted-foreground">{getSocialIcon(social.type)}</span>
                    <span className="text-sm font-medium text-foreground capitalize flex-1">
                      {social.type}
                    </span>
                    <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                  </a>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Dealership */}
          <Card>
            <CardHeader className="pb-0">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Building className="h-4 w-4 text-accent" />
                Loja
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <div>
                <p className="font-semibold text-foreground text-sm">
                  {vehicle.dealership.tradeName || vehicle.dealership.name}
                </p>
                {vehicle.dealership.address && (
                  <p className="text-xs text-muted-foreground mt-0.5">{vehicle.dealership.address}</p>
                )}
              </div>
              <div className="space-y-1.5">
                {vehicle.dealership.email && (
                  <a
                    href={`mailto:${vehicle.dealership.email}`}
                    className="block text-xs text-accent hover:underline"
                  >
                    {vehicle.dealership.email}
                  </a>
                )}
                {vehicle.dealership.phone && (
                  <a
                    href={`tel:${vehicle.dealership.phone}`}
                    className="block text-xs text-accent hover:underline"
                  >
                    {vehicle.dealership.phone}
                  </a>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Audit trail */}
          <Card>
            <CardHeader className="pb-0">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-accent" />
                Histórico
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3 text-xs">
              <div>
                <p className="text-muted-foreground mb-0.5">Criado por</p>
                <p className="font-semibold text-foreground">{vehicle.metadata.createdBy.name}</p>
                <p className="text-muted-foreground">
                  {new Date(vehicle.metadata.createdAt).toLocaleString('pt-BR')}
                </p>
              </div>
              <div className="border-t border-border pt-3">
                <p className="text-muted-foreground mb-0.5">Última atualização</p>
                <p className="font-semibold text-foreground">{vehicle.metadata.updatedBy.name}</p>
                <p className="text-muted-foreground">
                  {new Date(vehicle.metadata.updatedAt).toLocaleString('pt-BR')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Excluir veículo"
        description="Tem certeza que deseja excluir este veículo? Esta ação não pode ser desfeita."
        onConfirm={handleDelete}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={3000}
        />
      )}
    </div>
  )
}
